/**
 * Shared building blocks for the redesigned Public Sentiment blocks (SPEC §1):
 * block chrome with the partial layer and the screen-only Table toggle,
 * coverage glyphs, stance chips, legends, notes and the house table.
 *
 * Chrome (rules, labels, text) reads `roleColor(theme, …)`; data marks read
 * the validated PSR palette (`psrVizPalette`). Text never wears a data
 * colour: a label inside a filled mark uses that fill's `on*` colour, every
 * other label uses theme ink.
 *
 * Layout is flex / table only: the paged deck de-fangs any inline style
 * containing the substring "grid" (see EditorialKpiRail).
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import { getThemeReaderMode, roleColor } from '../../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../../lib/reportSectionHeading';
import { psrVizPalette, psrStanceColors, type PsrVizPalette } from '../../../../../lib/sentiment/psrVizPalette';
import type { PsrCoverageState } from '../../../../../lib/sentiment/publicSentimentMetaV2Contract';
import {
  PSR_COVERAGE_DEFINITIONS,
  PSR_COVERAGE_LABELS,
  psrReasonLabel,
  psrReasonShortLabel,
} from '../../../../../lib/sentiment/psrReportView';
import { EditorialCallout } from '../../../report/editorial';

/** The apparatus face (eyebrows, numerals, chart text). */
export const PSR_APPARATUS = 'var(--report-apparatus-font, var(--report-body-font, inherit))';
/** The reading face (block titles, row labels, notes). */
export const PSR_SERIF = 'var(--report-body-font, inherit)';
/** SVG charts are drawn at the 688px A4 print measure and scale with the column. */
export const PSR_CHART_WIDTH = 688;

export function psrPalette(theme: ReportTheme): PsrVizPalette {
  return psrVizPalette(getThemeReaderMode(theme));
}

/* ── Icons (currentColor) ──────────────────────────────────────────────── */

export function PsrInfoIcon({ size = 12 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <circle cx="6" cy="6" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <line x1="6" y1="5.4" x2="6" y2="8.6" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="6" cy="3.6" r="0.8" fill="currentColor" />
    </svg>
  );
}

export function PsrOmitIcon({ size = 12 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <circle cx="6" cy="6" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <line x1="2.8" y1="9.2" x2="9.2" y2="2.8" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

export function PsrQuestionIcon({ size = 20 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <circle cx="10" cy="10" r="8.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7.4 7.6 A2.7 2.7 0 1 1 11.2 10.1 C10.4 10.5 10 11 10 11.9 L10 12.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10" cy="14.8" r="1.05" fill="currentColor" />
    </svg>
  );
}

function PsrTableIcon(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" style={{ display: 'block' }}>
      <rect x="1" y="1.5" width="10" height="9" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <line x1="1" y1="4.6" x2="11" y2="4.6" stroke="currentColor" strokeWidth="1.2" />
      <line x1="1" y1="7.6" x2="11" y2="7.6" stroke="currentColor" strokeWidth="1.2" />
      <line x1="4.6" y1="4.6" x2="4.6" y2="10.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

/* ── Coverage glyphs (glyph + word, never hue) ─────────────────────────── */

export function CoverageGlyph({ state, size = 10 }: { state: PsrCoverageState; size?: number }): React.ReactElement {
  const common = { width: size, height: size, viewBox: '0 0 10 10', 'aria-hidden': true as const, style: { display: 'block', flex: 'none' } };
  switch (state) {
    case 'full':
      return <svg {...common}><circle cx="5" cy="5" r="4" fill="currentColor" /></svg>;
    case 'partial':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M 5 1 A 4 4 0 0 0 5 9 Z" fill="currentColor" />
        </svg>
      );
    case 'none':
      return <svg {...common}><circle cx="5" cy="5" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.3" /></svg>;
    case 'failed':
      return <svg {...common}><path d="M1.6 1.6 L8.4 8.4 M8.4 1.6 L1.6 8.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
    case 'unreported':
    default:
      return <svg {...common}><circle cx="5" cy="5" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeDasharray="1.7 1.5" /></svg>;
  }
}

export function CoverageMark({
  theme,
  state,
  fontSize = REPORT_TEXT_PX.label,
  weight = 600,
  ink = 'body',
  label,
}: {
  theme: ReportTheme;
  state: PsrCoverageState;
  fontSize?: number;
  weight?: number;
  ink?: 'body' | 'ink' | 'muted';
  label?: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      data-coverage-state={state}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontFamily: PSR_APPARATUS, fontSize, fontWeight: weight, color: roleColor(theme, ink) }}
    >
      <CoverageGlyph state={state} />
      {label ?? PSR_COVERAGE_LABELS[state]}
    </span>
  );
}

/** The five-glyph key (SPEC §1): glyph + word + definition. */
export function CoverageLegend({ theme, states }: { theme: ReportTheme; states?: PsrCoverageState[] }): React.ReactElement {
  const list = states ?? (['full', 'partial', 'none', 'failed', 'unreported'] as PsrCoverageState[]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', alignItems: 'center' }} data-testid="psr-coverage-legend">
      {list.map(state => (
        <span key={state} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <CoverageMark theme={theme} state={state} ink="ink" />
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, color: roleColor(theme, 'muted') }}>{PSR_COVERAGE_DEFINITIONS[state]}</span>
        </span>
      ))}
    </div>
  );
}

/* ── Tags, notes, callouts ─────────────────────────────────────────────── */

export function PsrPartialTag({ theme, label = 'Partial' }: { theme: ReportTheme; label?: string }): React.ReactElement {
  return (
    <span data-testid="psr-partial-tag" style={{ display: 'inline-flex' }}>
      <CoverageMark theme={theme} state="partial" ink="muted" label={label} />
    </span>
  );
}

/** Small explanatory line under a chart (faint serif). */
export function PsrNote({ theme, children, italic = false, testId }: { theme: ReportTheme; children: React.ReactNode; italic?: boolean; testId?: string }): React.ReactElement {
  return (
    <p data-testid={testId} style={{ margin: 0, fontFamily: PSR_SERIF, fontSize: REPORT_TEXT_PX.label, lineHeight: 1.6, color: roleColor(theme, 'faint'), fontStyle: italic ? 'italic' : undefined }}>
      {children}
    </p>
  );
}

/** Callout label with a leading icon (inherits the callout's label colour). */
export function PsrCalloutLabel({ icon = 'info', children }: { icon?: 'info' | 'omit'; children: React.ReactNode }): React.ReactElement {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: PSR_APPARATUS }}>
      {icon === 'omit' ? <PsrOmitIcon /> : <PsrInfoIcon />}
      <span>{children}</span>
    </span>
  );
}

/** The pale "caveat" callout used for every partial-coverage and truth note. */
export function PsrCaveat({ theme, label, icon, children, testId }: {
  theme: ReportTheme; label: React.ReactNode; icon?: 'info' | 'omit'; children: React.ReactNode; testId?: string;
}): React.ReactElement {
  return (
    <div data-testid={testId}>
      <EditorialCallout theme={theme} tone="caveat" label={<PsrCalloutLabel icon={icon}>{label}</PsrCalloutLabel>}>
        {children}
      </EditorialCallout>
    </div>
  );
}

/** Unfilled note (e.g. "Low volume"): a muted labelled paragraph. */
export function PsrPlainNote({ theme, label, icon = 'info', children, testId }: {
  theme: ReportTheme; label: React.ReactNode; icon?: 'info' | 'omit'; children: React.ReactNode; testId?: string;
}): React.ReactElement {
  return (
    <div data-testid={testId} data-layout-group="callout" role="note" style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: roleColor(theme, 'muted') }}>
        <PsrCalloutLabel icon={icon}>{label}</PsrCalloutLabel>
      </div>
      <div style={{ fontFamily: PSR_SERIF, fontSize: 13, lineHeight: 1.6, color: roleColor(theme, 'body') }}>{children}</div>
    </div>
  );
}

/* ── Legend ────────────────────────────────────────────────────────────── */

export interface PsrLegendItem { key: string; swatch: React.ReactNode; label: React.ReactNode }

export function PsrSwatch({ color, border, width = 10, height = 10 }: { color: string; border?: string; width?: number; height?: number }): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-block', boxSizing: 'border-box', width, height, borderRadius: 2, background: color, border: border ? `1px solid ${border}` : undefined, flex: 'none' }}
    />
  );
}

export function PsrLegend({ theme, items, dividerBefore }: { theme: ReportTheme; items: PsrLegendItem[]; dividerBefore?: string[] }): React.ReactElement {
  return (
    <div data-testid="psr-legend" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px' }}>
      {items.map(item => (
        <React.Fragment key={item.key}>
          {dividerBefore?.includes(item.key) && <span aria-hidden="true" style={{ width: 1, height: 14, background: roleColor(theme, 'rule'), flex: 'none' }} />}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, color: roleColor(theme, 'body'), whiteSpace: 'nowrap' }}>
            {item.swatch}
            {item.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Opposed / neutral statement / supportive | unscored — the stance key. */
export function StanceLegend({ theme, withStance = true }: { theme: ReportTheme; withStance?: boolean }): React.ReactElement {
  const p = psrPalette(theme);
  const unscored: PsrLegendItem = { key: 'unscored', swatch: <PsrSwatch color={p.unscored} />, label: 'Unscored (not in the index)' };
  return (
    <PsrLegend
      theme={theme}
      dividerBefore={withStance ? ['unscored'] : []}
      items={withStance ? [
        { key: 'opposed', swatch: <PsrSwatch color={p.opposed} />, label: 'Opposed' },
        { key: 'neutral', swatch: <PsrSwatch color={p.neutral} />, label: 'Neutral statement' },
        { key: 'supportive', swatch: <PsrSwatch color={p.supportive} />, label: 'Supportive' },
        unscored,
      ] : [unscored]}
    />
  );
}

/* ── Block chrome ──────────────────────────────────────────────────────── */

export interface PsrBlockProps {
  theme: ReportTheme;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Adds the ◐ Partial tag (the partial layer); never hides data. */
  partial?: boolean;
  /** A table view of the chart. When present, a screen-only Table toggle shows. */
  table?: React.ReactNode;
  /** Partial-layer caveat, shown under the chart when `partial`. */
  caveat?: React.ReactNode;
  testId: string;
  children: React.ReactNode;
  /** Wrap the chart body as one atomic exhibit (default true). */
  exhibit?: boolean;
  /** Where the partial caveat sits (the design leads tables with it). */
  caveatPosition?: 'top' | 'bottom';
}

/**
 * One chart/table block: hairline top rule, small-caps title + meta line,
 * right-aligned ◐ Partial tag and Table toggle, body, then the partial
 * caveat. Spans both columns in a two-column preset.
 */
export function PsrBlock({ theme, title, subtitle, partial = false, table, caveat, testId, children, exhibit = true, caveatPosition = 'bottom' }: PsrBlockProps): React.ReactElement {
  const [showTable, setShowTable] = React.useState(false);
  const accent = roleColor(theme, 'accent');
  const body = showTable && table ? table : children;
  const caveatNode = partial && caveat != null
    ? <PsrCaveat theme={theme} label="Partial coverage" testId={`${testId}-caveat`}>{caveat}</PsrCaveat>
    : null;
  return (
    <div
      data-testid={testId}
      data-psr-block={testId}
      data-psr-partial={partial ? 'true' : undefined}
      data-span-columns
      style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div data-layout-group="head-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: '1 1 16rem' }}>
            <div style={{ fontFamily: PSR_SERIF, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: roleColor(theme, 'ink'), lineHeight: 1.3 }}>
              {title}
            </div>
            {subtitle != null && (
              <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, color: roleColor(theme, 'muted'), lineHeight: 1.4 }}>{subtitle}</span>
            )}
          </div>
          {(partial || table) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 'none', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {partial && <PsrPartialTag theme={theme} />}
              {table && (
                <button
                  type="button"
                  data-screen-only-cta
                  data-testid={`${testId}-table-toggle`}
                  className="no-print"
                  aria-pressed={showTable}
                  onClick={() => setShowTable(v => !v)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, color: accent, background: 'transparent', border: 0, padding: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  <PsrTableIcon />
                  {showTable ? 'Chart' : 'Table'}
                </button>
              )}
            </div>
          )}
        </div>
        {caveatPosition === 'top' && caveatNode}
        {exhibit && !showTable ? <div data-layout-group="exhibit" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{body}</div> : body}
      </div>
      {caveatPosition === 'bottom' && caveatNode}
    </div>
  );
}

/** A small-caps group heading with a meta line (serif label + apparatus detail). */
export function PsrGroupHeading({ theme, label, detail }: { theme: ReportTheme; label: React.ReactNode; detail?: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ fontFamily: PSR_SERIF, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: roleColor(theme, 'ink'), lineHeight: 1.3 }}>{label}</div>
      {detail != null && <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 500, color: roleColor(theme, 'muted') }}>{detail}</span>}
    </div>
  );
}

/* ── House table with group rows ───────────────────────────────────────── */

export interface PsrTableColumn {
  header: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Apparatus face + tabular figures. */
  numeric?: boolean;
}
export interface PsrTableGroup {
  key: string;
  label?: React.ReactNode;
  detail?: React.ReactNode;
  /** A full-width note row under the group heading (e.g. "none established"). */
  note?: React.ReactNode;
  rows: Array<{ key: string; cells: React.ReactNode[]; muted?: boolean }>;
}

export function PsrTable({ theme, columns, groups, caption, testId, firstColumnStrong = true }: {
  theme: ReportTheme;
  columns: PsrTableColumn[];
  groups: PsrTableGroup[];
  caption?: React.ReactNode;
  testId?: string;
  firstColumnStrong?: boolean;
}): React.ReactElement | null {
  const rowCount = groups.reduce((n, g) => n + g.rows.length + (g.label != null ? 1 : 0) + (g.note != null ? 1 : 0), 0);
  if (rowCount === 0) return null;
  const ink = roleColor(theme, 'ink');
  const hair = roleColor(theme, 'hair');
  const splittable = rowCount >= 7;
  const cellBase: React.CSSProperties = { padding: '10px 12px', borderBottom: `1px solid ${hair}`, verticalAlign: 'top', color: roleColor(theme, 'body'), lineHeight: 1.5, overflowWrap: 'break-word', hyphens: 'none' };
  return (
    <table
      data-testid={testId}
      data-editorial-table
      data-table-splittable={splittable ? 'true' : undefined}
      style={{
        width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', borderBottom: `1.5px solid ${ink}`,
        fontSize: 'var(--editorial-table-fs, var(--report-body-size, 0.79rem))',
        breakInside: splittable ? 'auto' : 'avoid', pageBreakInside: splittable ? 'auto' : 'avoid',
      }}
    >
      {caption != null && (
        <caption style={{ captionSide: 'top', textAlign: 'left', padding: '0 0 8px', fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, color: ink }}>{caption}</caption>
      )}
      <colgroup>
        {columns.map((col, i) => <col key={i} style={col.width != null ? { width: col.width } : undefined} />)}
      </colgroup>
      <thead>
        <tr>
          {columns.map((col, i) => (
            <th
              key={i}
              scope="col"
              style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: roleColor(theme, 'muted'), padding: '8px 12px', borderBottom: `1.5px solid ${ink}`, verticalAlign: 'bottom', textAlign: col.align ?? 'left' }}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {groups.map(group => (
          <React.Fragment key={group.key}>
            {group.label != null && (
              <tr style={{ breakInside: 'avoid', breakAfter: 'avoid' }}>
                <td colSpan={columns.length} style={{ background: roleColor(theme, 'subtleBg'), padding: '8px 12px', borderBottom: `1px solid ${hair}` }}>
                  <PsrGroupHeading theme={theme} label={group.label} detail={group.detail} />
                </td>
              </tr>
            )}
            {group.note != null && (
              <tr style={{ breakInside: 'avoid' }}>
                <td colSpan={columns.length} style={{ ...cellBase, fontStyle: 'italic', color: roleColor(theme, 'faint') }}>{group.note}</td>
              </tr>
            )}
            {group.rows.map(row => (
              <tr key={row.key} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                {columns.map((col, c) => (
                  <td
                    key={c}
                    style={{
                      ...cellBase,
                      textAlign: col.align ?? 'left',
                      ...(col.numeric ? { fontFamily: PSR_APPARATUS, fontVariantNumeric: 'tabular-nums', color: ink } : {}),
                      ...(c === 0 && firstColumnStrong ? { fontWeight: 600, color: row.muted ? roleColor(theme, 'muted') : ink } : {}),
                    }}
                  >
                    {row.cells[c]}
                  </td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

/** Muted italic placeholder cell text (e.g. "No coverage row, no recorded failure"). */
export function PsrMutedText({ theme, children }: { theme: ReportTheme; children: React.ReactNode }): React.ReactElement {
  return <span style={{ fontStyle: 'italic', color: roleColor(theme, 'faint') }}>{children}</span>;
}

/** "3 of 7" style count with a muted tail. */
export function PsrCountOf({ theme, value, of, size = 13 }: { theme: ReportTheme; value: React.ReactNode; of?: React.ReactNode; size?: number }): React.ReactElement {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, fontFamily: PSR_APPARATUS, fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ fontSize: size, fontWeight: 600, color: roleColor(theme, 'ink') }}>{value}</span>
      {of != null && <span style={{ fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'muted') }}>of {of}</span>}
    </span>
  );
}

/* ── Stance chips ──────────────────────────────────────────────────────── */

function StanceGlyph({ stance, color }: { stance: -1 | 0 | 1; color: string }): React.ReactElement {
  const common = { width: 10, height: 10, viewBox: '0 0 10 10', 'aria-hidden': true as const, style: { display: 'block', flex: 'none' } };
  if (stance < 0) return <svg {...common}><path d="M1.2 2.6 L8.8 2.6 L5 8.4 Z" fill={color} /></svg>;
  if (stance > 0) return <svg {...common}><path d="M1.2 7.8 L8.8 7.8 L5 2 Z" fill={color} /></svg>;
  return <svg {...common}><circle cx="5" cy="5.2" r="3.2" fill={color} /></svg>;
}
function HollowSquare({ color }: { color: string }): React.ReactElement {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <rect x="1.9" y="2.1" width="6.2" height="6.2" rx="0.6" fill="none" stroke={color} strokeWidth="1.3" />
    </svg>
  );
}

export const PSR_STANCE_WORDS: Readonly<Record<'-1' | '0' | '1', string>> = Object.freeze({ '-1': 'Opposed', '0': 'Neutral', '1': 'Supportive' });

export type StanceChipProps =
  /** Local stance: filled chip (the only chip that means local stance). */
  | { theme: ReportTheme; kind: 'local'; stance: -1 | 0 | 1 }
  /** Local unscored: pale chip; the reason prints under it. */
  | { theme: ReportTheme; kind: 'unscored' }
  /** Context stance (assessed reports only): outlined, never local stance. */
  | { theme: ReportTheme; kind: 'context'; stance: -1 | 0 | 1 }
  /** Context without a stance: outlined chip carrying the short reason. */
  | { theme: ReportTheme; kind: 'context-unscored'; reasonCode: string }
  /** No-dated-local-stance report: a context row reads "Not assessed". */
  | { theme: ReportTheme; kind: 'not-assessed' };

const chipBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, boxSizing: 'border-box', borderRadius: 3,
  fontFamily: PSR_APPARATUS, fontSize: 11.5, fontWeight: 600, lineHeight: '16px', whiteSpace: 'nowrap',
};

export function StanceChip(props: StanceChipProps): React.ReactElement {
  const { theme } = props;
  const p = psrPalette(theme);
  if (props.kind === 'local') {
    const { fill, text } = psrStanceColors(p, props.stance);
    return (
      <span data-stance-chip="local" data-stance={props.stance} style={{ ...chipBase, padding: '2px 8px 2px 6px', background: fill, color: text }}>
        <StanceGlyph stance={props.stance} color={text} />
        {PSR_STANCE_WORDS[String(props.stance) as '-1' | '0' | '1']}
      </span>
    );
  }
  if (props.kind === 'unscored') {
    return (
      <span data-stance-chip="unscored" style={{ ...chipBase, padding: '2px 8px 2px 6px', background: p.unscored, color: p.onUnscored }}>
        <HollowSquare color={p.onUnscored} />
        Unscored
      </span>
    );
  }
  const outline: React.CSSProperties = { ...chipBase, padding: '1px 7px 1px 5px', background: 'transparent', border: `1px solid ${roleColor(theme, 'rule')}`, color: roleColor(theme, 'muted') };
  const muted = roleColor(theme, 'muted');
  if (props.kind === 'context') {
    return (
      <span data-stance-chip="context" data-stance={props.stance} style={outline}>
        <StanceGlyph stance={props.stance} color={muted} />
        {PSR_STANCE_WORDS[String(props.stance) as '-1' | '0' | '1']}
      </span>
    );
  }
  if (props.kind === 'context-unscored') {
    return (
      <span data-stance-chip="context-unscored" style={outline}>
        <HollowSquare color={muted} />
        {psrReasonShortLabel(props.reasonCode)}
      </span>
    );
  }
  return <span data-stance-chip="not-assessed" style={outline}>Not assessed</span>;
}

/** Pale reason chip used in the status block: "Agenda only, no testimony · 2". */
export function ReasonCountChip({ theme, reasonCode, count }: { theme: ReportTheme; reasonCode: string; count: number }): React.ReactElement {
  const p = psrPalette(theme);
  return (
    <span data-reason-chip={reasonCode} style={{ ...chipBase, padding: '2px 8px 2px 6px', background: p.unscored, color: p.onUnscored }}>
      <HollowSquare color={p.onUnscored} />
      {psrReasonLabel(reasonCode)} · {count}
    </span>
  );
}
